import { useEffect, useMemo, useState } from 'react';
import { api, type FuelSiteLine, type FuelSitePreview, type FuelSubsidyMode } from '../lib/api';
import { DataTable, ErrorBox, money, type Column, type Row } from '../components/DataTable';
import { kindLabel } from '../lib/driverKind';

/**
 * 재무추가 **유가보조금 사이트 자료** 대량등록 — 「버스유가보조금사이트 경유주유내역」 파일을 그대로 올린다.
 *
 * 카드 대량등록과 갈리는 곳: 사이트 카드(BIN 4265)는 편성 주유카드와 **다른 카드**라 카드번호로는 하나도 안 붙는다.
 * 그래서 **차량번호 + 주유일**로 그날의 편성을 찾는다(서버 `resultDailyFuelSite.ts`). 법인별로 쪼개져 오는 파일을
 * 한 번에 여러 개 올릴 수 있고, 칸 위치는 서버가 머리글 이름으로 찾는다(A열이 비어 한 칸 밀린 파일 실측).
 *
 * 지급(유류보조금) 기본 = 두 줄 — 사이트 「보조금」 칸 그대로(전 차량) + 회사분(관리기사B만 · 공급가(결제금액 ÷ 1.1) × 7%, 비고 「_회사」).
 * 08·09월 실제 등록·명세가 이 모양이다(10-20 prod 대조). 한쪽만 넣는 버튼도 둔다.
 */

type Busy = 'parse' | 'check' | 'commit' | null;

const PICKED: Record<string, string> = { acct: '회계계정', driver: '실기사', latest: '여럿 → 최신' };

export default function ResultDailyFuelSiteUpload({ driveMon, onDone }: { driveMon: string; onDone: () => void }) {
  const yymm = `${driveMon.slice(2, 4)}.${driveMon.slice(5, 7)}`;
  const [mode, setMode] = useState<FuelSubsidyMode>('both');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [commentUse, setCommentUse] = useState('');
  const [commentSub, setCommentSub] = useState('');
  const [commentCo, setCommentCo] = useState('');
  const [files, setFiles] = useState<{ name: string; rows: string[][] }[]>([]);
  /** DYOPS 「유류 거래」에 올린 사이트 자료를 원천으로(파일 다시 안 올림 · 2026-11-03). */
  const [fromDyops, setFromDyops] = useState(false);
  const [fixes, setFixes] = useState<Record<string, number>>({});
  const [skipErrors, setSkipErrors] = useState(false);
  const [allowDup, setAllowDup] = useState(false);
  const [preview, setPreview] = useState<FuelSitePreview | null>(null);
  const [busy, setBusy] = useState<Busy>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ created: number; plus: number; minus: number; skipped: { siteCarNo: string; pay: number; errors: string[] }[] } | null>(null);

  // 기간·비고 기본값은 정산월을 따른다(바꾼 뒤에도 정산월이 바뀌면 다시 기본으로).
  useEffect(() => {
    const [y, m] = driveMon.split('-').map(Number) as [number, number];
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    setFrom(`${driveMon}-01`); setTo(`${driveMon}-${String(last).padStart(2, '0')}`);
    setCommentUse(`${yymm}월 유류보조금카드사용`);
    setCommentSub(`${yymm}월 유류보조금`);
    setCommentCo(`${yymm}월 유류보조금_회사`);
    setPreview(null); setDone(null);
  }, [driveMon, yymm]);
  useEffect(() => { setPreview(null); }, [mode]);

  async function pick(e: React.ChangeEvent<HTMLInputElement>) {
    const list = [...(e.target.files ?? [])];
    if (!list.length) return;
    setBusy('parse'); setError(null); setPreview(null); setDone(null);
    try {
      const XLSX = await import('xlsx');
      const read = await Promise.all(list.map(async (f) => {
        const wb = XLSX.read(await f.arrayBuffer(), { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]!];
        if (!sheet) throw new Error(`${f.name}: 시트를 찾을 수 없습니다.`);
        // 보이는 글자 그대로(금액 쉼표 · 날짜 글자) — 해석은 서버가 한다.
        const rows = XLSX.utils.sheet_to_json<string[]>(sheet, { header: 1, raw: false, defval: '' })
          .map((r) => r.map((v) => String(v ?? '')));
        return { name: f.name, rows };
      }));
      setFromDyops(false);
      setFiles((cur) => [...cur.filter((c) => !read.some((r) => r.name === c.name)), ...read]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
      e.target.value = '';
    }
  }

  const payload = (extra?: { fixes?: Record<string, number>; dyops?: boolean }) => {
    const dy = extra?.dyops ?? fromDyops;
    return {
      driveMon, from, to, subsidyMode: mode, commentUse, commentSub, commentCo,
      files: dy ? [] : files, source: dy ? ('dyops' as const) : undefined,
      fixes: extra?.fixes ?? fixes, skipErrors, allowDup,
    };
  };

  async function check(nextFixes?: Record<string, number>, dyops?: boolean) {
    setBusy('check'); setError(null); setDone(null);
    try {
      setPreview(await api.previewResultDailyFuelSite(payload({ fixes: nextFixes, dyops })));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPreview(null);
    } finally { setBusy(null); }
  }

  async function commit() {
    if (!preview) return;
    const ok = window.confirm(
      `${driveMon} 정산월에 ${preview.willCreate.toLocaleString('ko-KR')}줄을 넣습니다 — 공제 ${money(preview.okPay)} · 지급 ${money(preview.okSubsidy)}` +
        ` (사이트분 ${money(preview.okSiteSubsidy)} · 회사분 ${money(preview.okCoSubsidy)})` +
        (preview.errorCars ? `\n편성을 못 찾은 ${preview.errorCars}대(${money(preview.errPay)})는 빼고 넣습니다 — 따로 처리하세요.` : ''),
    );
    if (!ok) return;
    setBusy('commit'); setError(null);
    try {
      const r = await api.commitResultDailyFuelSite(payload());
      setDone({ created: r.created, plus: r.plus, minus: r.minus, skipped: r.skipped });
      setFiles([]); setFromDyops(false); setPreview(null); setFixes({});
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally { setBusy(null); }
  }

  function chooseFix(key: string, carId: number | null) {
    const next = { ...fixes };
    if (carId === null) delete next[key]; else next[key] = carId;
    setFixes(next);
    void check(next);
  }

  const okLines = useMemo(() => (preview?.lines ?? []).filter((l) => l.errors.length === 0), [preview]);
  const ngLines = useMemo(() => (preview?.lines ?? []).filter((l) => l.errors.length > 0), [preview]);

  const okCols: Column[] = [
    { key: 'siteCarNo', label: '사이트 차량번호', render: (r) => {
      const l = r as unknown as FuelSiteLine;
      return (
        <span className="whitespace-nowrap">
          {l.siteCarNo}
          {l.carMatch === 'fixed' && <Tag tone="amber" title="사이트 번호가 버스인에 없어 고른 한 글자 차이 차량">고침</Tag>}
          {l.carMatch === 'fixed' && (
            <button className="ml-1 text-xs text-sky-700 underline" disabled={busy !== null}
                    onClick={() => chooseFix(l.key, null)}>되돌리기</button>
          )}
          {l.carMatch === 'embedded' && <Tag tone="amber" title="번호판이 이름 속에 든 다른 차량에 붙었습니다 — 맞는지 보세요">다른 이름 차량</Tag>}
          {l.split > 1 && <Tag tone="sky" title="월 중간에 편성이 바뀌어 주유일로 나눴습니다">나눔 {l.split}</Tag>}
        </span>
      );
    } },
    { key: 'dates', label: '주유일', render: (r) => {
      const l = r as unknown as FuelSiteLine;
      return <span className="whitespace-nowrap text-xs">{l.firstDate?.slice(5)}{l.lastDate !== l.firstDate ? ` ~ ${l.lastDate?.slice(5)}` : ''} · {l.count}건</span>;
    } },
    { key: 'carAllocId', label: '편성', align: 'right' },
    { key: 'carNo', label: '버스인 차량' },
    { key: 'driverName', label: '기사', render: (r) => {
      const l = r as unknown as FuelSiteLine;
      return (
        <span className="whitespace-nowrap">
          {l.driverName}
          {l.pickedBy === 'latest' && <Tag tone="amber" title="그날 살아 있는 실기사 편성이 여럿 — 번호 큰 쪽을 골랐습니다">{PICKED.latest}</Tag>}
          {l.outside > 0 && <Tag tone="amber" title="주유일에 살아 있는 편성이 없어 그 달 편성 중에서 골랐습니다">편성 기간 밖 {l.outside}건</Tag>}
        </span>
      );
    } },
    { key: 'driverKind', label: '기사구분', render: (r) => kindLabel(r.driverKind as string | null) },
    { key: 'ownerName', label: '차주' },
    { key: 'grpName', label: '소속' },
    { key: 'companies', label: '사이트 회사명', render: (r) => (r.companies as string[]).join(', ') },
    { key: 'pay', label: '공제(결제금액)', align: 'right', render: (r) => money(r.pay) },
    ...(mode !== 'company' ? [{ key: 'subsidy', label: '지급 사이트분', align: 'right' as const, render: (r: Row) => (Number(r.subsidy) > 0 ? money(r.subsidy) : '—') }]
      : [{ key: 'siteSubsidy', label: '(참고) 사이트 보조금', align: 'right' as const, render: (r: Row) => money(r.siteSubsidy) }]),
    ...(mode !== 'site' ? [{ key: 'coSubsidy', label: '지급 회사분', align: 'right' as const, render: (r: Row) => (Number(r.coSubsidy) > 0 ? money(r.coSubsidy) : '—') }] : []),
    { key: 'existing', label: '이미 있음', align: 'right', render: (r) => (Number(r.existing) > 0 ? <b className="text-red-700">{String(r.existing)}줄</b> : '') },
  ];

  const ngCols: Column[] = [
    { key: 'siteCarNo', label: '사이트 차량번호' },
    { key: 'companies', label: '사이트 회사명', render: (r) => (r.companies as string[]).join(', ') },
    { key: 'pay', label: '결제금액', align: 'right', render: (r) => money(r.pay) },
    { key: 'siteSubsidy', label: '사이트 보조금', align: 'right', render: (r) => money(r.siteSubsidy) },
    { key: 'errors', label: '사유', render: (r) => <span className="text-red-700">{(r.errors as string[]).join(' · ')}</span> },
    { key: 'suggestions', label: '한 글자 차이 차량', render: (r) => {
      const l = r as unknown as FuelSiteLine;
      if (!l.suggestions.length) return '';
      return (
        <select className="input py-0.5 text-xs" value={fixes[l.key] ?? ''} disabled={busy !== null}
                onChange={(e) => chooseFix(l.key, e.target.value ? Number(e.target.value) : null)}>
          <option value="">고르지 않음</option>
          {l.suggestions.map((s) => <option key={s.carId} value={s.carId}>{s.carNo}</option>)}
        </select>
      );
    } },
  ];

  const canCommit = preview !== null && !preview.isFinished && preview.unreadableCount === 0 && preview.okLines > 0
    && (preview.errorCars === 0 || skipErrors) && (preview.dupLines === 0 || allowDup);

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-sm font-bold">{driveMon} 유가보조금 사이트 자료 등록</h2>
        <span className="text-xs text-ink-faint">
          「버스유가보조금사이트 경유주유내역」 파일 그대로 — <b>차량번호·주유일</b>로 그날 편성을 찾습니다 · 승인거래만 더합니다 · 법인별 파일 여러 개 한 번에
        </span>
      </div>

      {error && <ErrorBox message={error} />}

      <div className="flex flex-wrap items-center gap-3 text-xs text-ink-soft">
        <label className="flex items-center gap-1">주유 기간
          <input type="date" className="input py-1 text-xs" value={from} max="9999-12-31"
                 onChange={(e) => { setFrom(e.target.value); setPreview(null); }} disabled={busy !== null} />
          ~
          <input type="date" className="input py-1 text-xs" value={to} max="9999-12-31"
                 onChange={(e) => { setTo(e.target.value); setPreview(null); }} disabled={busy !== null} />
        </label>
        <span className="flex items-center gap-1">지급(유류보조금)
          <button className={mode === 'both' ? 'btn text-xs' : 'btn-ghost text-xs'} disabled={busy !== null}
                  onClick={() => setMode('both')}
                  title="08·09월과 같은 두 줄 — 사이트 「보조금」 그대로(전 차량) + 회사분(관리기사B만 · 공급가(결제금액 ÷ 1.1) × 7%, 2027-01 정산부터 5%)">
            사이트분 + 회사분
          </button>
          <button className={mode === 'company' ? 'btn text-xs' : 'btn-ghost text-xs'} disabled={busy !== null}
                  onClick={() => setMode('company')}
                  title="관리기사B(차주소유기사)만 · 공급가(결제금액 ÷ 1.1) × 회사분 율(2026-12 정산까지 7% · 2027-01부터 5%) — 사이트분 줄 없이">
            회사분만
          </button>
          <button className={mode === 'site' ? 'btn text-xs' : 'btn-ghost text-xs'} disabled={busy !== null}
                  onClick={() => setMode('site')}
                  title="사이트 「보조금」 칸 그대로 · 전 차량(관리기사A 포함) — 회사분 줄 없이">
            사이트분만
          </button>
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-ink-soft">
        <label className="flex items-center gap-1">공제 비고
          <input className="input w-56 py-1 text-xs" value={commentUse} disabled={busy !== null}
                 onChange={(e) => { setCommentUse(e.target.value); setPreview(null); }} />
        </label>
        {mode !== 'company' && (
          <label className="flex items-center gap-1">사이트분 비고
            <input className="input w-56 py-1 text-xs" value={commentSub} disabled={busy !== null}
                   onChange={(e) => { setCommentSub(e.target.value); setPreview(null); }} />
          </label>
        )}
        {mode !== 'site' && (
          <label className="flex items-center gap-1">회사분 비고
            <input className="input w-56 py-1 text-xs" value={commentCo} disabled={busy !== null}
                   onChange={(e) => { setCommentCo(e.target.value); setPreview(null); }} />
          </label>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button className={fromDyops ? 'btn text-xs' : 'btn-ghost text-xs'} disabled={busy !== null}
                title="DYOPS 「유류 거래」에 올린 유가보조금 사이트 파일을 그대로 씁니다 — 파일을 다시 올리지 않아도 됩니다"
                onClick={() => { setFromDyops(true); setFiles([]); setPreview(null); void check(undefined, true); }}>
          {busy === 'check' && fromDyops ? '불러오는 중…' : 'DYOPS에서 불러오기'}
        </button>
        <span className="text-xs text-ink-faint">또는</span>
        <label className="btn-ghost cursor-pointer text-xs">
          {busy === 'parse' ? '읽는 중…' : '경유주유내역 파일 고르기'}
          <input type="file" accept=".xls,.xlsx" multiple className="hidden" onChange={pick} disabled={busy !== null} />
        </label>
        {files.map((f) => (
          <span key={f.name} className="flex items-center gap-1 rounded bg-surface px-2 py-0.5 text-xs">
            {f.name}
            <button className="text-ink-faint hover:text-red-700" title="이 파일 빼기" disabled={busy !== null}
                    onClick={() => { setFiles((cur) => cur.filter((c) => c.name !== f.name)); setPreview(null); }}>✕</button>
          </span>
        ))}
        {files.length > 0 && (
          <button className="btn-ghost text-xs" onClick={() => void check()} disabled={busy !== null}>
            {busy === 'check' ? '맞춰 보는 중…' : '검증'}
          </button>
        )}
      </div>

      {preview && (
        <>
          {preview.dyops && (
            <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-900">
              DYOPS 유류 거래 자료 {preview.dyops.rows.toLocaleString('ko-KR')}줄 · 파일 {preview.dyops.files.length}개
              {preview.dyops.lastUploadedAt && <> · 마지막 올림 {preview.dyops.lastUploadedAt}</>}
              {preview.dyops.uploadedBy.length > 0 && <> · {preview.dyops.uploadedBy.map((e) => e.split('@')[0]).join(', ')}</>}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-line bg-surface px-3 py-2 text-sm">
            <span>차량 <b>{preview.cars.toLocaleString('ko-KR')}</b>대</span>
            <span className="text-emerald-700">넣을 묶음 {preview.okLines.toLocaleString('ko-KR')}</span>
            <span className={preview.errorCars ? 'font-bold text-red-700' : 'text-ink-faint'}>못 찾음 {preview.errorCars}</span>
            <span>공제 <b>{money(preview.okPay)}</b></span>
            <span>지급 <b>{money(preview.okSubsidy)}</b>
              {preview.subsidyMode !== 'company' && <> · 사이트분 {money(preview.okSiteSubsidy)}</>}
              {preview.subsidyMode !== 'site' && <> · 회사분({preview.rate}) {money(preview.okCoSubsidy)}</>}
            </span>
            <span className="text-ink-soft">넣을 줄 <b>{preview.willCreate.toLocaleString('ko-KR')}</b></span>
            <span className="text-xs text-ink-faint">
              파일 결제금액 합 {money(preview.sumPay)} = 넣음 {money(preview.okPay)} + 못 찾음 {money(preview.errPay)}
            </span>
          </div>

          <div className="space-y-0.5 text-xs text-ink-soft">
            {preview.files.map((f) => (
              <div key={f.name}>
                {f.name} — 승인 {f.approved.toLocaleString('ko-KR')}건 · 취소 {f.cancelled}건 제외
                {f.outOfRange > 0 && <> · <b className="text-amber-800">기간 밖 {f.outOfRange}건 제외</b></>}
                {f.minDate && <> · 파일 주유일 {f.minDate} ~ {f.maxDate}</>}
              </div>
            ))}
          </div>

          {preview.isFinished && <ErrorBox message="관리자 정산이 완료된 달이라 대량 등록할 수 없습니다. 마감을 먼저 푸세요." />}
          {preview.unreadableCount > 0 && (
            <ErrorBox message={`읽지 못한 승인거래 줄이 ${preview.unreadableCount}줄 있습니다 — ${preview.unreadable.slice(0, 5).map((u) => `${u.file} ${u.row}행 ${u.why}`).join(' · ')}`} />
          )}
          {(preview.embeddedCars > 0 || preview.latestPicked > 0 || preview.outsideLines > 0 || preview.fixedCars > 0) && (
            <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              확인할 줄: {[
                preview.fixedCars && `차량번호 고침 ${preview.fixedCars}`,
                preview.embeddedCars && `다른 이름 차량에 붙음 ${preview.embeddedCars}`,
                preview.latestPicked && `편성 여럿 → 최신 ${preview.latestPicked}`,
                preview.outsideLines && `주유일에 편성 없음 ${preview.outsideLines}`,
              ].filter(Boolean).join(' · ')} — 아래 표의 주황 표시를 보세요.
              {preview.splitCars > 0 && <> 월 중간 편성 변경으로 주유일에 따라 나눈 차량 {preview.splitCars}대.</>}
            </p>
          )}
          {preview.dupLines > 0 && (
            <label className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <input type="checkbox" checked={allowDup} onChange={(e) => setAllowDup(e.target.checked)} disabled={busy !== null} />
              이 달에 이미 유류법인카드이용대금이 있는 편성이 <b>{preview.dupLines}</b>개입니다(두 번 올리는 것 아닌지 확인) — 확인했으면 체크: 이미 있어도 넣기
            </label>
          )}

          {ngLines.length > 0 && (
            <>
              <p className="text-sm text-red-700">
                편성을 못 찾은 차량 <b>{ngLines.length}대</b>({money(preview.errPay)}) — 한 글자 차이 차량이 맞으면 고르면 다시 맞춰 봅니다.
                그대로 빼고 넣으면 이 차량들은 따로(새 항목) 넣어야 합니다.
              </p>
              <DataTable columns={ngCols} rows={ngLines as unknown as Row[]} loading={false} tableId="upload.fuelsite.errors" />
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={skipErrors} onChange={(e) => setSkipErrors(e.target.checked)} disabled={busy !== null} />
                못 찾은 차량 빼고 등록
              </label>
            </>
          )}

          {okLines.length > 0 && (
            <DataTable columns={okCols} rows={okLines as unknown as Row[]} loading={false} tableId="upload.fuelsite.match" />
          )}

          {canCommit && (
            <button className="btn" onClick={commit} disabled={busy !== null}>
              {busy === 'commit' ? '등록 중…' : `${preview.willCreate.toLocaleString('ko-KR')}줄 등록 실행`}
            </button>
          )}
        </>
      )}

      {done && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <b>{done.created.toLocaleString('ko-KR')}줄을 등록했습니다.</b> 지급 {money(done.plus)} · 공제 {money(done.minus)}
          {done.skipped.length > 0 && (
            <div className="mt-1 text-red-800">
              뺀 차량 {done.skipped.length}대 — 따로 넣으세요: {done.skipped.map((s) => `${s.siteCarNo} ${money(s.pay)}`).join(' · ')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Tag({ tone, title, children }: { tone: 'amber' | 'sky'; title: string; children: React.ReactNode }) {
  const cls = tone === 'amber' ? 'bg-amber-50 text-amber-800' : 'bg-sky-50 text-sky-800';
  return <span className={`ml-1 rounded px-1.5 py-0.5 text-xs ${cls}`} title={title}>{children}</span>;
}
